/**
 * Shared route configuration - Single source of truth for portal route permissions
 *
 * This file defines all portal routes with their permissions, navigation metadata,
 * and feature flags. Both PortalRouter and NavData use this configuration to ensure
 * consistency between routing permissions and navigation display.
 */

export type AllowedRole = 'staff' | 'superUser' | 'orgAdmin' | 'teamLeader' | 'director' | 'regularUser';

export interface RoutePermission {
  /** Route path (relative to /app) */
  path: string;
  /** Full path including /app prefix */
  fullPath: string;
  /** Roles allowed to access this route */
  allowedRoles: AllowedRole[];
  /** Navigation title (if shown in nav) */
  navTitle?: string;
  /** Navigation icon (if shown in nav) */
  navIcon?: any;
  /** Section title for navigation grouping */
  sectionTitle?: string;
  /** Feature flag name required for this route */
  featureFlag?: string;
  /** Whether route requires a closed assessment period */
  requiresClosedAP?: boolean;
  /** Whether this route should appear in navigation */
  showInNav?: boolean;
  /** Sort order for navigation; lower numbers appear earlier */
  sortOrder?: number;
  /** Child routes (for nested navigation) */
  children?: RoutePermission[];
}

/**
 * Portal route permissions configuration
 *
 * Based on access control rules:
 * - Admin (SUPERUSER): Everything
 * - Staff: Everything EXCEPT system maintenance (Features, Scheduled Tasks, Email)
 * - Org Admin (account_type=9): Dashboard, Analytics, Org Goals, Foundations, Assessments,
 *   Team Management, Outcomes, Copilot, Org Settings, Import Review, Metrics, Team Projects
 * - Team Leader (account_type=3): Dashboard, Analytics, Team Projects, Culture Hub (if FF enabled),
 *   Assessments, Metrics. NOT: Foundations, Outcomes, Org Goals, Copilot, Team Management
 */
export const PORTAL_ROUTES: Record<string, RoutePermission> = {
  dashboard: {
    path: 'dashboard',
    fullPath: '/app/dashboard',
    allowedRoles: ['superUser', 'staff', 'orgAdmin', 'director'],
    navTitle: 'Dashboard',
    sectionTitle: 'Analysis',
    sortOrder: 1,
    showInNav: true,
  },
  cultureHub: {
    path: 'culture-hub',
    fullPath: '/app/culture-hub',
    allowedRoles: ['teamLeader', 'orgAdmin'],
    showInNav: false, // Don't show parent in nav, only children
  },
  cultureHubAnalytics: {
    path: 'culture-hub/analytics',
    fullPath: '/app/culture-hub/analytics',
    allowedRoles: ['teamLeader', 'orgAdmin'],
    navTitle: 'Dashboard',
    sectionTitle: 'Culture Hub',
    sortOrder: 2,
    showInNav: true,
  },
  cultureHubPillars: {
    path: 'culture-hub/pillars',
    fullPath: '/app/culture-hub/pillars',
    allowedRoles: ['teamLeader', 'orgAdmin'],
    navTitle: 'Team Feedback',
    sortOrder: 3,
    showInNav: true,
  },
  cultureHubInterventions: {
    path: 'culture-hub/interventions',
    fullPath: '/app/culture-hub/interventions',
    allowedRoles: ['teamLeader', 'orgAdmin'],
    sectionTitle: 'Action Plans',
    navTitle: 'Focus Plan',
    sortOrder: 4,
    showInNav: true,
  },
  cultureHubFocusPlanRecommendations: {
    path: 'culture-hub/interventions/focus-plan-recommendations',
    fullPath: '/app/culture-hub/interventions/focus-plan-recommendations',
    allowedRoles: ['teamLeader', 'orgAdmin'],
    showInNav: false,
  },
  cultureHubBio: {
    path: 'culture-hub/bio',
    fullPath: '/app/culture-hub/bio',
    allowedRoles: ['teamLeader', 'orgAdmin'],
    navTitle: 'Bio',
    featureFlag: 'Culture Hub Bio',
    sortOrder: 5,
    showInNav: true,
  },
  analytics: {
    path: 'analytics',
    fullPath: '/app/analytics',
    allowedRoles: ['superUser', 'staff', 'orgAdmin', 'director'],
    navTitle: 'Analytics',
    requiresClosedAP: true,
    sortOrder: 6,
    showInNav: true,
  },
  outcomes: {
    path: 'outcomes',
    fullPath: '/app/outcomes',
    allowedRoles: ['superUser', 'staff', 'orgAdmin', 'director'],
    navTitle: 'Outcomes',
    requiresClosedAP: true,
    sortOrder: 7,
    showInNav: true,
  },
  copilot: {
    path: 'copilot',
    fullPath: '/app/copilot',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    navTitle: 'Copilot',
    requiresClosedAP: true,
    sortOrder: 8,
    showInNav: true,
  },
  foundations: {
    path: 'foundations',
    fullPath: '/app/foundations',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    navTitle: 'Foundations',
    requiresClosedAP: true,
    sectionTitle: 'Action Plans',
    sortOrder: 9,
    showInNav: true,
  },
  orgGoals: {
    path: 'org-goals',
    fullPath: '/app/org-goals',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    navTitle: 'Organizational Goals',
    requiresClosedAP: true,
    sortOrder: 10,
    showInNav: true,
  },
  leaderboard: {
    path: 'leaderboard',
    fullPath: '/app/leaderboard',
    allowedRoles: ['superUser', 'staff', 'orgAdmin', 'teamLeader'],
    navTitle: 'Leaderboard',
    requiresClosedAP: true,
    sortOrder: 11,
    showInNav: true,
  },

/* TP: This feature is deprecated for the moment, until we figure out what to do with it 
If we get rid of it, remove from routes. */
  
/*   teamProjects: {
    path: 'team-projects',
    fullPath: '/app/team-projects',
    allowedRoles: ['superUser', 'staff', 'orgAdmin', 'director'],
    navTitle: 'Team Projects',
    requiresClosedAP: true,
    sortOrder: 10,
    showInNav: true,
  }, */
  assessments: {
    path: 'assessments',
    fullPath: '/app/assessments',
    allowedRoles: ['superUser', 'staff', 'orgAdmin', 'director'],
    navTitle: 'Culture',
    sectionTitle: 'Assessments',
    sortOrder: 12,
    showInNav: true,
  },
  pulseSurveys: {
    path: 'pulse-surveys',
    fullPath: '/app/pulse-surveys',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    navTitle: 'Pulse',
    featureFlag: 'Pulse Surveys',
    sortOrder: 13,
    showInNav: true,
  },
  teamManagement: {
    path: 'team-management',
    sectionTitle: 'Organization',
    fullPath: '/app/team-management',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    navTitle: 'Members',
    sortOrder: 14,
    showInNav: true,
  },  
  rosterReview: {
    path: 'team-management/roster-review',
    fullPath: '/app/team-management/roster-review',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    sortOrder: 14,
    showInNav: false,
  },
  teams: {
    path: 'teams',
    fullPath: '/app/teams',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    navTitle: 'Teams',
    sortOrder: 15,
    showInNav: true,
  },  
  knowledgeBase: {
    path: 'knowledge-base',
    fullPath: '/app/knowledge-base',
    allowedRoles: ['superUser', 'staff', 'orgAdmin', 'teamLeader', 'director', 'regularUser'],
    navTitle: 'Knowledge Base',
    sectionTitle: 'Learning Resources',
    sortOrder: 16,
    showInNav: true,
  },
  knowledgeBaseResources: {
    path: 'knowledge-base/resources',
    fullPath: '/app/knowledge-base/resources',
    allowedRoles: ['superUser', 'staff', 'orgAdmin', 'teamLeader', 'director', 'regularUser'],
    showInNav: false,
  },
  mobileExperience: {
    path: 'mobile',
    fullPath: '/app/mobile',
    allowedRoles: ['superUser', 'staff', 'orgAdmin', 'teamLeader', 'director', 'regularUser'],
    showInNav: false,
  },
  orgSettings: {
    path: 'org-settings',
    fullPath: '/app/org-settings',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    sortOrder: 17,
    showInNav: false, // Not in main nav, accessed via other means
  },
  importReview: {
    path: 'import-review',
    fullPath: '/app/import-review',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    sortOrder: 18,
    showInNav: false, // Not in main nav
  },
  teamImportReview: {
    path: 'teams/import-review',
    fullPath: '/app/teams/import-review',
    allowedRoles: ['superUser', 'staff', 'orgAdmin'],
    showInNav: false,
  },
  glossary: {
    path: 'glossary',
    fullPath: '/app/glossary',
    allowedRoles: ['orgAdmin', 'teamLeader'],
    navTitle: 'Glossary',
    sortOrder: 19,
    showInNav: true,
  },

  summary: {
    path: 'summary',
    fullPath: '/app/summary',
    allowedRoles: ['teamLeader', 'orgAdmin'],
    navTitle: 'Guided Summary',
    sectionTitle: 'Insights',
    sortOrder: 4,
    showInNav: true,
  },

  accessDenied: {
    path: 'access-denied',
    fullPath: '/app/access-denied',
    allowedRoles: [], // Special route, no permission check
    sortOrder: 20,
    showInNav: false,
  },
};

/**
 * Helper function to check if a user role has permission for a route
 */
export function hasRoutePermission(
  route: RoutePermission,
  userRoles: {
    isStaff: boolean;
    isSuperUser: boolean;
    isOrgAdmin: boolean;
    isTeamLeader: boolean;
    isDirector?: boolean;
    isRegularUser: boolean;
  }
): boolean {
  // SuperUsers get access to routes with 'superUser' OR 'staff' in allowedRoles
  if (userRoles.isSuperUser && (route.allowedRoles.includes('superUser') || route.allowedRoles.includes('staff'))) {
    return true;
  }

  // Check if any of the user's roles match the allowed roles
  if (route.allowedRoles.includes('staff') && userRoles.isStaff) return true;
  if (route.allowedRoles.includes('orgAdmin') && userRoles.isOrgAdmin) return true;
  if (route.allowedRoles.includes('teamLeader') && userRoles.isTeamLeader) return true;
  if (route.allowedRoles.includes('director') && userRoles.isDirector) return true;
  if (route.allowedRoles.includes('regularUser') && userRoles.isRegularUser) return true;

  return false;
}

/**
 * Get all routes that a user has permission to access
 */
export function getRoutesForUser(userRoles: {
  isStaff: boolean;
  isSuperUser: boolean;
  isOrgAdmin: boolean;
  isTeamLeader: boolean;
  isDirector?: boolean;
  isRegularUser: boolean;
}): RoutePermission[] {
  return Object.values(PORTAL_ROUTES)
    .filter(route => hasRoutePermission(route, userRoles))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
} 

/**
 * Convert route config to NavData format for backward compatibility
 * This allows NavData.ts to use the shared config while maintaining existing structure
 * Note: Icons are handled separately in NavData.ts since they're UI-specific
 */
export function convertRouteToNavData(route: RoutePermission): any {
  const navItem: any = {
    id: route.path.replace(/\//g, '-'),
    link: route.fullPath,
    title: route.navTitle,
    section_title: route.sectionTitle,
    requires_closed_AP: route.requiresClosedAP || false,
    regular_member: route.allowedRoles.includes('regularUser'),
    team_admin: route.allowedRoles.includes('teamLeader'),
    director: route.allowedRoles.includes('director'),
    org_admin: route.allowedRoles.includes('orgAdmin'),
    superuser: route.allowedRoles.includes('superUser') || route.allowedRoles.includes('staff'),
    staff: route.allowedRoles.includes('staff'),
  };

  if (route.featureFlag) {
    navItem.feature_flag = route.featureFlag;
  }

  // Include sort order on nav item to allow UI consumers to sort if needed
  navItem.sort_order = route.sortOrder || 0;

  if (route.children && route.children.length > 0) {
    navItem.children = route.children
      .filter(child => child.showInNav)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(convertRouteToNavData);
  }

  return navItem;
}
